import { DownloadOutlined } from '@ant-design/icons';
import { type ExportTemplateDTOType } from '@framework/api-client/api/backoffice/schemas/exportTemplateDTOType.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { ModalDialog } from '@framework/ui/components/modal/ModalDialog.tsx';
import { Alert, type ButtonProps, Flex, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import {
  ClosingDivider,
  ConfirmDivider,
  ConfirmText,
  ExportCurrentPageCheckbox,
  ExportFileButtonStyled,
  ExportModalDivider,
  MenuItemTrigger,
} from './ExportFileButton.styles.ts';
import {
  type ExportFileEntity,
  type ExportFileField,
  type ExportFileType,
} from './ExportFileTypes.ts';
import { ExportFieldFieldsSelect } from './field-select/ExportFileFieldSelect.tsx';
import { ExportTemplateSection } from './template/ExportTemplateSection.tsx';
import { useExportTemplate } from './template/useExportTemplate.ts';
import { useExportFileStorageData } from './useExportFileStorageData.ts';

/**
 * Props per {@link ExportFileButton}.
 *
 * Lo stato UI (modale aperta, campi selezionati, flag "esporta tutto")
 * è gestito internamente dal componente.
 * Il consumer deve solo fornire la callback `onExport` (tipicamente da
 * {@link useExportFile}) e i metadati necessari alla visualizzazione.
 */
export interface ExportFileButtonProps {
  /** Tipo di file da esportare (csv, xlsx, geojson, pdf). */
  type: ExportFileType;
  /** Entità esportata — usata per label, template e localStorage. */
  entity: ExportFileEntity;
  /**
   * Callback asincrona invocata al click su "Esporta" nella modale.
   * Riceve i campi selezionati e il flag exportAll.
   * Restituisce `true` se l'export è riuscito, `false` altrimenti.
   * Se `false`, la modale resta aperta per permettere un nuovo tentativo.
   */
  onExport: (params: {
    fields: string[];
    exportAll: boolean;
  }) => Promise<boolean>;
  /** Configurazione dei campi esportabili. */
  fieldsConfiguration: {
    fields: ExportFileField[] | null | undefined;
    areLoading: boolean;
    maxSelectables?: number;
    areRequired?: boolean;
  };
  /** Paginazione corrente — mostrata nella checkbox "scarica solo pagina corrente". */
  pagination?: { page?: number; pageSize?: number };
  /** Stile del pulsante trigger (default: `'text'`). */
  buttonType?: ButtonProps['type'];
  /** Se `true`, il trigger viene renderizzato come `<span>` per l'uso dentro un menu Dropdown. */
  asMenuItem?: boolean;
  /** Discriminatore opzionale per la chiave localStorage (es. un ID tipo albo). */
  storageDiscriminator?: string;
  /** Tipo di template di esportazione. Se omesso, i template sono disabilitati. */
  templateType?: ExportTemplateDTOType;
}

/**
 * Pulsante + modale per l'esportazione di file.
 *
 * Gestisce internamente lo stato della modale (apertura/chiusura),
 * la selezione dei campi, il flag "esporta tutto", i template e
 * il salvataggio in localStorage.
 *
 * Il download vero e proprio è delegato alla callback `onExport`
 * fornita dal consumer (tipicamente tramite {@link useExportFile}).
 */
export function ExportFileButton(props: Readonly<ExportFileButtonProps>) {
  const {
    type,
    entity,
    onExport,
    pagination,
    fieldsConfiguration,
    buttonType = 'text',
    asMenuItem,
    storageDiscriminator,
    templateType,
  } = props;

  const { t } = useTranslation();
  const isMobile = useBreakpoint(down('sm'));

  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportAll, setExportAll] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [showMaxExceededAlert, setShowMaxExceededAlert] = useState(false);

  useEffect(() => {
    if (
      fieldsConfiguration.maxSelectables != null &&
      selected.length > fieldsConfiguration.maxSelectables
    ) {
      setSelected(selected.slice(0, fieldsConfiguration.maxSelectables));
      setShowMaxExceededAlert(true);
    }
  }, [selected, fieldsConfiguration.maxSelectables]);

  const localStorageData = useExportFileStorageData({
    entity,
    type,
    storageDiscriminator,
    onSelect: setSelected,
  });
  const typeTitle = type === 'geojson' ? 'GeoJSON' : type.toUpperCase();
  const entityTitle = entity.charAt(0).toUpperCase() + entity.slice(1);

  const templateConfig = useExportTemplate({ templateType });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (templateType != null) {
        await templateConfig.saveTemplate(selected);
      } else {
        localStorageData.update(selected);
      }
    } catch (error) {
      message.error(
        getNetworkErrorMessage(
          error,
          t(
            'export-file-button.error.save-template',
            'Si è verificato un errore durante il salvataggio del template.',
          ),
        ),
      );
    }
    try {
      const success = await onExport({ fields: selected, exportAll });
      if (success) {
        setExportAll(false);
        setIsOpen(false);
      }
    } catch (error) {
      message.error(
        getNetworkErrorMessage(
          error,
          t(
            'export-file-button.error.export',
            "Si è verificato un errore durante l'esportazione.",
          ),
        ),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const title = t('export-file-button.export-title', 'Esporta {{typeTitle}}', {
    typeTitle,
  });

  const menuItemTrigger = asMenuItem ? (
    <MenuItemTrigger
      onClick={() => setIsOpen(true)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(true);
        }
      }}
    >
      {title}
    </MenuItemTrigger>
  ) : (
    <ExportFileButtonStyled
      aria-label={title}
      icon={isMobile && <DownloadOutlined aria-hidden="true" />}
      type={buttonType}
      onClick={() => {
        setIsOpen(true);
      }}
    >
      {!isMobile && title}
    </ExportFileButtonStyled>
  );

  return (
    <>
      {menuItemTrigger}
      <ModalDialog
        title={t(
          'export-file-button.export-entity',
          `Esportazione {{typeTitle}} {{entityTitle}}`,
          { typeTitle, entityTitle },
        )}
        open={isOpen}
        width={600}
        onCancel={e => {
          if (isExporting) return;

          setExportAll(false);
          setIsOpen(false);
        }}
        okText={t('export-file-button.ok-text', 'Esporta')}
        onOk={handleExport}
        okButtonProps={{
          loading: isExporting || templateConfig.isSaving,
          disabled: fieldsConfiguration.areRequired && selected.length === 0,
        }}
        cancelText={t('export-file-button.cancel-text', 'Annulla')}
        cancelButtonProps={{
          disabled: isExporting || templateConfig.isSaving,
        }}
        maskClosable={!(isExporting || templateConfig.isSaving)}
        afterClose={() => {
          setShowMaxExceededAlert(false);
          if (!(templateType != null || localStorageData.saveFlag)) {
            setSelected([]);
          }
        }}
        destroyOnClose
      >
        <Flex vertical gap={8}>
          <ExportModalDivider />
          {showMaxExceededAlert && (
            <Alert
              type="warning"
              showIcon
              closable
              onClose={() => setShowMaxExceededAlert(false)}
              message={t(
                'export-file-button.max-selectable-exceeded',
                'Il limite massimo è cambiato. Sono stati mantenuti i primi {{max}} campi.',
                { max: fieldsConfiguration.maxSelectables },
              )}
            />
          )}
          <ExportTemplateSection
            templateConfig={templateConfig}
            showTemplates={templateType != null}
            onSelect={setSelected}
            localStorageData={localStorageData}
          />
          <ExportFieldFieldsSelect
            isLoading={fieldsConfiguration.areLoading}
            onSelect={setSelected}
            fields={fieldsConfiguration.fields}
            selected={selected}
            maxSelectable={fieldsConfiguration.maxSelectables}
            requireFields={fieldsConfiguration.areRequired}
          />
          {pagination != null && (
            <ExportCurrentPageCheckbox
              checked={!exportAll}
              onChange={v => {
                setExportAll(!v.target.checked);
              }}
            >
              {t(
                'export-file-button.download-current-page-checkbox',
                'Scarica solo elementi presenti nella pagina corrente (pag. {{page}}) (max. {{pageSize}} {{entity}})',
                {
                  pageSize: pagination.pageSize,
                  entity,
                  page: pagination.page,
                },
              )}
            </ExportCurrentPageCheckbox>
          )}
          <ConfirmDivider />
          <ConfirmText>
            {t(
              'export-file-button.confirm-export-final-text',
              'Stai per esportare i dati trovati dai filtri di ricerca attualmente attivi. A seconda dei filtri utilizzati questa operazione potrebbe richiedere diverso tempo. Continuare?',
            )}
          </ConfirmText>
          <ClosingDivider />
        </Flex>
      </ModalDialog>
    </>
  );
}
